import React, { useEffect, useState, useRef } from "react";
import _, { isNil, isEmpty, get } from "lodash";
import { StatusCodes } from "http-status-codes";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

import ClientFormComponent from "./components/ClientFormComponent";
import ClinicFormComponent from "./components/ClinicFormComponent";
import ReferrerFormComponent from "./components/ReferrerFormComponent";
import ReferralFormComponent from "./components/ReferralFormComponent";

import { rawReferralToManagedIncomingReferral } from "../../models";
import {
  createWorkflowByIncomingReferral,
  createWorkflowByIncomingReferralR,
  saveReferralData,
  saveReferralDataR,
} from "../../services/referralService";
import { CATEGORY_OPTIONS, STATUS, TIME_FORMAT } from "../../../../constants";
import { getRawReferrals } from "../../rawReferralSlice";
import { getRawReferralsAI } from "../../rawReferralAISlice";
import ActionPanel from "./components/ActionPanel";
import UploadTestResultModal from "./components/UploadTestResultModal";
import { fetchClinics, fetchReferrers } from "../../../../components/appSlice";
import { deleteReferralById } from "../../../../services/rawReferrals";
import { FormSelectField, FormWeekToAppointmentField } from "./components/FormComponents";

const { DEFAULT, SUCCEED, FAILED, LOADING } = STATUS;
const isDataFromPMS = process.env.REACT_APP_DATA_SOURCE === "pms-api";
const dataFromPMSText = isDataFromPMS ? "pms" : "coreplus";

const ReferralDataForm = ({ referral: rawReferral, source }) => {
  const dispatch = useDispatch();
  const [referral, setReferral] = useState(null);
  const [processingStatus, setProcessingStatus] = useState({ status: DEFAULT });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const formRef = useRef(null);
  const locations = useSelector((state) => state.app.locations.filter((l) => l.name !== "Anywhere Healthcare"));

  useEffect(() => {
    if (rawReferral) {
      setReferral(rawReferralToManagedIncomingReferral(rawReferral));
      if (rawReferral.clientExisting === "Existing") {
        setProcessingStatus({
          status: FAILED,
          errors: [`The client is duplicated, please check in ${dataFromPMSText}`],
        });
      }
    }
  }, [rawReferral]);

  const handleValueChange = (root) => {
    return (key) => (value) => {
      setReferral((prev) => ({
        ...prev,
        [root]: {
          ...prev[root],
          [key]: value,
        },
      }));
    };
  };

  const updateResource = (root) => (resource) => {
    setReferral((prev) => {
      return {
        ...prev,
        [root]: {
          ...resource,
        },
      };
    });
  };

  const validateSavedReferralDataR = () => {
    let errorMsgArray = [];

    if (isNil(referral)) {
      errorMsgArray.push("Referral can not be empty");
      return errorMsgArray;
    }

    ["client", "letterFile"].forEach((key) => {
      if (isEmpty(referral[key])) {
        errorMsgArray.push(`${key} section can not be empty`);
      }
    });

    const requiredFields = ["firstName", "lastName"];
    const { client } = referral;

    [...requiredFields, "dateOfBirth"].forEach((key) => {
      if (isEmpty(client[key])) {
        errorMsgArray.push(`Client ${key} can not be empty`);
      }
    });

    return errorMsgArray;
  };

  const validateSavedReferralData = () => {
    let errorMsgArray = [];
    if (isNil(referral)) {
      errorMsgArray.push("Referral can not be empty");
      return errorMsgArray;
    }

    ["client", "clinic", "referrer", "referral", "letterFile"].forEach((key) => {
      if (isEmpty(referral[key])) {
        errorMsgArray.push(`${key} section can not be empty`);
      }
    });
    if (errorMsgArray.length > 0) {
      return errorMsgArray;
    }

    const requiredFields = ["firstName", "lastName"];
    const { client, clinic, referrer, referral: referralSection, letterFile } = referral;

    [...requiredFields, "dateOfBirth", "gender"].forEach((key) => {
      if (isEmpty(client[key])) {
        errorMsgArray.push(`Client ${key} can not be empty`);
      }
    });

    if (isEmpty(clinic.name)) {
      errorMsgArray.push(`Clinic name can not be empty`);
    }

    [...requiredFields, "providerNumber"].forEach((key) => {
      if (isEmpty(referrer[key])) {
        errorMsgArray.push(`Referrer ${key} can not be empty`);
      }
    });

    if (isEmpty(referralSection.startDate)) {
      errorMsgArray.push(`Referral startDate can not be empty`);
    }

    if (isEmpty(referralSection.expiredDate) && isEmpty(referralSection.expiredDateByMonth)) {
      errorMsgArray.push(`Referral expiredDate can not be empty`);
    }

    if (isEmpty(referralSection.id)) {
      errorMsgArray.push(`Practitioner can not be empty`);
    }

    ["id", "name"].forEach((key) => {
      if (isEmpty(letterFile[key])) {
        errorMsgArray.push(`Letter File ${key} can not be empty`);
      }
    });
    return errorMsgArray;
  };

  const handleUploadReferral = async (e) => {
    e.preventDefault();
    setProcessingStatus({ status: LOADING });

    const errors = validateSavedReferralDataR();
    if (errors.length > 0) {
      setProcessingStatus({ status: FAILED, errors });
      formRef.current.scrollTop = 0;
      return;
    }

    const { error, data } = await saveReferralDataR(referral, source);
    const incomingReferralRId = get(data, "id", "");
    console.log(`handleUploadReferral::::::incomingReferralRId ${incomingReferralRId}`);

    if (!isEmpty(error)) {
      let errorMessage = error.message;
      error?.response?.status === StatusCodes.BAD_REQUEST &&
        (errorMessage = `The client or referral is not existing in ${dataFromPMSText}.`);
      setProcessingStatus({ status: FAILED, errors: [errorMessage] });
      formRef.current.scrollTop = 0;
      return;
    }

    if (!isEmpty(incomingReferralRId) && isEmpty(error)) {
      const { error, data } = await createWorkflowByIncomingReferralR(incomingReferralRId);
      if (!isEmpty(data) && isEmpty(error)) {
        setProcessingStatus({ status: SUCCEED });
      }
      console.log(`handleUploadReferral::::::create r workflow id ${data}`);

      if (!isEmpty(error)) {
        setProcessingStatus({ status: FAILED, errors: [`Can not create workflow: ${error}`] });
      }
      formRef.current.scrollTop = 0;
      source === "ocr_clients" ? dispatch(getRawReferrals(source)) : dispatch(getRawReferralsAI(source));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setProcessingStatus({ status: LOADING });
    const errors = validateSavedReferralData();
    if (errors.length > 0) {
      setProcessingStatus({ status: FAILED, errors });
      formRef.current.scrollTop = 0;
      return;
    }

    const { error, data } = await saveReferralData(referral, source);
    const incomingReferralId = get(data, "id", "");
    if (!isEmpty(error)) {
      let errorMessage = error.message;
      error?.response?.status === StatusCodes.CONFLICT &&
        (errorMessage = `The client is duplicated, please check in ${dataFromPMSText}.`);
      // The PMS refused the referral and said why (e.g. the referrer has no real provider number).
      error?.response?.status === StatusCodes.UNPROCESSABLE_ENTITY &&
        get(error, "response.data.error") &&
        (errorMessage = get(error, "response.data.error"));
      setProcessingStatus({ status: FAILED, errors: [errorMessage] });
      formRef.current.scrollTop = 0;
      return;
    }

    if (!isEmpty(incomingReferralId) && isEmpty(error)) {
      const { error, data } = await createWorkflowByIncomingReferral(incomingReferralId);
      if (!isEmpty(data) && isEmpty(error)) {
        setProcessingStatus({ status: SUCCEED });
      }

      if (!isEmpty(error)) {
        setProcessingStatus({ status: FAILED, errors: [`Can not create workflow: ${error}`] });
      }
      formRef.current.scrollTop = 0;
      dispatch(getRawReferrals());
      dispatch(fetchReferrers());
      dispatch(fetchClinics());
    }
  };

  const handleUploadTheTestResult = async (e) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  const onUploadTestResultFinished = async () => {
    setIsModalOpen(false);
    source === "ocr_clients" ? dispatch(getRawReferrals(source)) : dispatch(getRawReferralsAI(source));
  };

  const handleDeleteReferral = async (e) => {
    e.preventDefault();
    setProcessingStatus({ status: LOADING });
    const { error: deleteError } = await deleteReferralById({ id: referral?.rawReferralId, source });

    if (!deleteError) {
      source === "ocr_clients" ? dispatch(getRawReferrals(source)) : dispatch(getRawReferralsAI(source));
      setProcessingStatus({ status: SUCCEED });
      return;
    }

    setProcessingStatus({ status: FAILED, errors: [`Delete Referral Letter failed: ${deleteError}`] });
  };

  const { status, errors } = processingStatus;
  const handleCategoryChange = (value) => setReferral({ ...referral, category: value });
  const handleWeeksToAppointmentChange = (value) => {
    const today = moment().startOf("day");
    const selectedDay = _.isEmpty(value) ? null : moment(value, TIME_FORMAT.DATE_WITH_DASH).startOf("day");
    // A past due date is valid: the referral is already overdue. Keep it; weeks may be negative (SE-803).
    // Floor rather than truncate so a date 1–6 days overdue is -1, not "0".
    const hasDate = Boolean(selectedDay && selectedDay.isValid());

    setReferral({
      ...referral,
      weeksToAppointment: hasDate ? String(Math.floor(selectedDay.diff(today, "days") / 7)) : "0",
      expectedAppointmentDate: hasDate ? value : null,
    });
  };

  const handleLocationChange = (value) =>
    setReferral({ ...referral, location: _.find(locations, (location) => location.locationId === value) ?? null });
  return (
    referral && (
      <>
        <UploadTestResultModal
          file={referral.letterFile}
          isModalOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onFinish={onUploadTestResultFinished}
          source={source}
        />
        <form ref={formRef} style={{ overflowY: "scroll", overflowX: "hidden", height: "calc(100vh - 180px)" }}>
          {status === FAILED && errors.length > 0 && (
            <div className="notification is-danger is-light is-small is-size-7 is-radiusless">
              {errors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </div>
          )}
          {status === SUCCEED && <div className="notification is-success is-light">All Set!</div>}
          <ClientFormComponent client={referral.client} handleValueChange={handleValueChange("client")} />
          <div className="columns">
            <FormSelectField
              onValueChange={handleCategoryChange}
              options={CATEGORY_OPTIONS}
              className="column column is-half"
              label="Category"
              value={referral.category}
            />
            <FormSelectField
              onValueChange={handleLocationChange}
              options={locations.map((location) => {
                return { label: location.name, value: location.locationId };
              })}
              className="column column is-half"
              label="Hospitals"
              value={referral.location?.locationId}
            />
          </div>
          <ClinicFormComponent
            clinic={referral.clinic}
            handleValueChange={handleValueChange("clinic")}
            updateResource={updateResource("clinic")}
          />
          <ReferrerFormComponent
            referrer={referral.referrer}
            clinicId={referral?.clinic?.id}
            handleValueChange={handleValueChange("referrer")}
            updateResource={updateResource("referrer")}
          />
          <ReferralFormComponent referral={referral.referral} handleValueChange={handleValueChange("referral")} />
          <div className="columns">
            <FormWeekToAppointmentField
              onValueChange={handleWeeksToAppointmentChange}
              defaultEmptyOption={false}
              className="column is-half"
              label="Weeks To Appointment"
              value={referral.weeksToAppointment ?? "0"}
              dateValue={referral.expectedAppointmentDate ?? ""}
            />
          </div>
          <ActionPanel
            referral={referral}
            handleDeleteReferral={handleDeleteReferral}
            status={status}
            handleSave={handleSave}
            handleUploadTheTestResult={handleUploadTheTestResult}
            handleUploadReferral={handleUploadReferral}
            source={source}
          />
        </form>
      </>
    )
  );
};

export default ReferralDataForm;
